import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

// TICKET-01502: payments that exist in Tripletex without a matching invoice —
// open credit posts on the customer ledger, plus money parked on interim accounts.
// The root cause is usually a DJ sale that was never transferred, so the primary
// action is sending the matched sale to Tripletex (per match, or in bulk for
// confident matches); the accountant then matches the open payment against the
// new invoice in Tripletex, and "Oppdater fra Tripletex" brings the paid status
// back to DJ. Registering the payment directly on the DJ sale stays available as
// a secondary action for sales that should never reach Tripletex.

function formatAmount(amount) {
    if (amount === null || amount === undefined || amount === '') return '-';
    return util.format(amount, 2, ',', ' ');
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    if (typeof dateStr === 'string' && dateStr.length >= 10) {
        const parts = dateStr.substring(0, 10).split('-');
        if (parts.length === 3) {
            return `${parts[2]}.${parts[1]}.${parts[0]}`;
        }
    }
    return dateStr;
}

const REASON_LABELS = {
    invoiceNumber: 'Fakturanr',
    restAmount: 'Restbeløp',
    totalAmount: 'Totalbeløp',
    name: 'Navn',
    date: 'Dato',
};

// A confident payment whose best match still needs to be sent to Tripletex.
function needsTransfer(p) {
    return p.confident && !p.registeredOnSale && p.candidates.length > 0
        && !p.candidates[0].tripletexTransferred;
}

@observer
class TripletexOpenPosts extends Component {
    constructor(props) {
        super(props);
        this.state = {
            working: {},       // postingId -> true while a POST for the row runs
            results: {},       // postingId -> { ok, message }
            bulkRunning: false,
        };
    }

    setResult = (postingId, ok, message) => {
        const { working, results } = this.state;
        results[postingId] = { ok, message };
        working[postingId] = false;
        this.setState({ working, results });
    }

    beginWork = (postingId) => {
        const { working, results } = this.state;
        if (working[postingId]) return false;
        working[postingId] = true;
        delete results[postingId];
        this.setState({ working, results });
        return true;
    }

    doSend = async (payment, candidate) => {
        const { stores, getTripletexCreateError } = this.props;
        const { tripletexStore } = stores;
        if (!this.beginWork(payment.postingId)) return null;
        const response = await tripletexStore.createInvoice(candidate.id);
        // TICKET-01186: the backend replies HTTP 200 even when Tripletex rejected
        // the invoice — the parser from allVisitsTable extracts the real reason.
        const createError = getTripletexCreateError
            ? getTripletexCreateError(response)
            : (response?.data?.value?.id ? null : (response?.message || 'Overføring feilet.'));
        const ok = !createError;
        this.setResult(payment.postingId, ok, ok
            ? `Salg #${candidate.saleNumber} overført til Tripletex. Koble innbetalingen mot fakturaen i Tripletex.`
            : createError);
        return ok;
    }

    sendToTripletex = async (payment, candidate) => {
        const dialogAction = beginDialogAction(this, `sendToTripletex:${payment.postingId}`);
        if (!dialogAction) return false;
        try {
            // eslint-disable-next-line no-alert
            if (!((await appConfirm(`Sende salg #${candidate.saleNumber} (${candidate.customerName}, ${formatAmount(candidate.restPayment)} kr) til Tripletex som faktura?\n\nEtterpå kobler regnskapsfører den åpne innbetalingen mot fakturaen i Tripletex, og «Oppdater fra Tripletex» henter betalingsstatusen tilbake.`)) && dialogAction.isCurrent())) {
                return;
            }
            const ok = await this.doSend(payment, candidate);
            if (ok && this.props.onRefresh) {
                await this.props.onRefresh();
            }
        } finally {
            dialogAction.finish();
        }
    }

    sendAllConfident = async () => {
        const dialogAction = beginDialogAction(this, 'sendAllConfident');
        if (!dialogAction) return false;
        try {
            const { stores, onRefresh } = this.props;
            const { tripletexStore } = stores;
            const data = tripletexStore.openPostsData;
            const toSend = (data?.openPayments || []).filter(needsTransfer);
            if (toSend.length === 0) return;
            const lines = toSend.slice(0, 15).map(p => `• Salg #${p.candidates[0].saleNumber} (${p.candidates[0].customerName}) ← ${formatAmount(Math.abs(p.amount))} kr`);
            if (toSend.length > 15) lines.push(`… og ${toSend.length - 15} til`);
            // eslint-disable-next-line no-alert
            if (!((await appConfirm(`Sende ${toSend.length} salg til Tripletex som faktura?\n\n${lines.join('\n')}\n\nEtterpå kobles innbetalingene mot fakturaene i Tripletex av regnskapsfører.`)) && dialogAction.isCurrent())) {
                return;
            }
            this.setState({ bulkRunning: true });
            const sentSaleIds = {};
            for (let i = 0; i < toSend.length; i += 1) {
                const payment = toSend[i];
                const candidate = payment.candidates[0];
                // Two payments can point at the same sale — send it only once; the
                // backend refuses re-transfers anyway (post-invoices.js 409).
                if (sentSaleIds[candidate.id]) {
                    this.setResult(payment.postingId, true, `Salg #${candidate.saleNumber} allerede sendt i denne runden.`);
                } else {
                    sentSaleIds[candidate.id] = true;
                    // eslint-disable-next-line no-await-in-loop
                    await this.doSend(payment, candidate);
                }
            }
            this.setState({ bulkRunning: false });
            if (onRefresh) {
                await onRefresh();
            }
        } finally {
            dialogAction.finish();
        }
    }

    registerPayment = async (payment, candidate) => {
        const dialogAction = beginDialogAction(this, `registerPayment:${payment.postingId}`);
        if (!dialogAction) return false;
        try {
            const { stores } = this.props;
            const { tripletexStore } = stores;
            const sum = formatAmount(Math.abs(payment.amount));
            // eslint-disable-next-line no-alert
            if (!((await appConfirm(`Registrere betaling på ${sum} kr på salg #${candidate.saleNumber} (${candidate.customerName}) UTEN å sende salget til Tripletex?\n\nSalget oppdateres bare i Dyrejournal. Den åpne posten i Tripletex forblir ukoblet.`)) && dialogAction.isCurrent())) {
                return;
            }
            if (!this.beginWork(payment.postingId)) return;
            const response = await tripletexStore.registerOpenPostPayment({
                saleId: candidate.id,
                postingId: payment.postingId,
                tripletexCustomerId: payment.tripletexCustomerId,
            });
            const ok = response?.status === 200;
            this.setResult(payment.postingId, ok, ok
                ? `Registrert på salg #${response.data?.saleNumber}${response.data?.kind === 'fullyPaid' ? ' (betalt)' : ' (delbetaling)'}`
                : (response?.message || 'Registrering feilet.'));
            if (ok && this.props.onRefresh) {
                await this.props.onRefresh();
            }
        } finally {
            dialogAction.finish();
        }
    }

    render() {
        const { stores, onClose, onRefresh, gotoSale } = this.props;
        const { working, results, bulkRunning } = this.state;
        const { tripletexStore } = stores;
        const data = tripletexStore.openPostsData;
        const loading = tripletexStore.openPostsLoading;
        const openPayments = data?.openPayments || [];
        const otherAccounts = (data?.otherAccounts || []).filter(a => a.count > 0);
        const summary = data?.summary || {};
        const transferableCount = openPayments.filter(needsTransfer).length;

        return (
            <div class='card mb-3'>
                <div class='card-header d-flex align-items-center justify-content-between'>
                    <strong>
                        <i class='fa-duotone fa-money-bill-transfer me-2' />
                        Åpne poster i Tripletex
                    </strong>
                    <div class='d-flex gap-2'>
                        {transferableCount > 0 && (
                            <button class='btn btn-sm btn-info rounded-pill' onClick={this.sendAllConfident} disabled={bulkRunning || loading}>
                                {bulkRunning ? <i class='fa-duotone fa-spinner fa-spin me-1' /> : <i class='fa-duotone fa-cloud-arrow-up me-1' />}
                                Send {transferableCount} sikre treff til Tripletex
                            </button>
                        )}
                        <button class='btn btn-sm btn-outline-primary rounded-pill' onClick={onRefresh} disabled={loading || bulkRunning}>
                            {loading ? <i class='fa-duotone fa-spinner fa-spin me-1' /> : <i class='fa-solid fa-rotate me-1' />}
                            Oppdater
                        </button>
                        <button class='btn btn-sm btn-outline-secondary rounded-pill' onClick={onClose}>
                            <i class='fa-solid fa-xmark me-1' />
                            Lukk
                        </button>
                    </div>
                </div>
                <div class='card-body'>
                    <p class='text-body-secondary' style='font-size: 0.9em;'>
                        Innbetalinger som ligger åpne i Tripletex uten en faktura å koble mot — som regel fordi
                        salget aldri ble overført fra Dyrejournal. Send det foreslåtte salget til Tripletex, la
                        regnskapsfører koble innbetalingen mot fakturaen der, og kjør «Oppdater fra Tripletex»
                        for å hente betalingsstatusen tilbake hit.
                    </p>

                    {loading && !data && (
                        <div class='text-center p-4'>
                            <i class='fa-duotone fa-spinner fa-spin fa-2x' />
                        </div>
                    )}

                    {data && <>
                        <div class='d-flex flex-wrap gap-3 mb-3' style='font-size: 0.9em;'>
                            {data.dateFrom && data.dateTo && (
                                <span class='badge bg-secondary-subtle text-secondary-emphasis rounded-pill' title='Fra/til-valget i salgslisten. Uten valg vises inneværende år så langt.'>
                                    <i class='fa-duotone fa-calendar-range me-1' />
                                    Periode {formatDate(data.dateFrom)} – {formatDate(data.dateTo)}
                                </span>
                            )}
                            <span class='badge bg-warning-subtle text-warning-emphasis rounded-pill'>
                                {summary.totalOpenPayments || 0} åpne innbetalinger ({formatAmount(Math.abs(summary.totalOpenPaymentsAmount || 0))} kr)
                            </span>
                            <span class='badge bg-success-subtle text-success-emphasis rounded-pill'>
                                {summary.confidentMatches || 0} sikre treff
                            </span>
                            <span class='badge bg-info-subtle text-info-emphasis rounded-pill'>
                                {summary.withCandidates || 0} med forslag
                            </span>
                            <span class='badge bg-danger-subtle text-danger-emphasis rounded-pill'>
                                {summary.withoutCandidates || 0} uten forslag
                            </span>
                            {summary.registeredInDj > 0 && (
                                <span class='badge bg-secondary-subtle text-secondary-emphasis rounded-pill'>
                                    {summary.registeredInDj} allerede registrert i Dyrejournal
                                </span>
                            )}
                            {data.openInvoiceSummary?.count > 0 && (
                                <span class='badge bg-secondary-subtle text-secondary-emphasis rounded-pill'>
                                    {data.openInvoiceSummary.count} ubetalte fakturaer i Tripletex ({formatAmount(data.openInvoiceSummary.sumAmount)} kr)
                                </span>
                            )}
                        </div>

                        {openPayments.length === 0 && (
                            <div class='alert alert-success'>
                                <i class='fa-solid fa-circle-check me-2' />
                                Ingen åpne innbetalinger uten faktura i Tripletex.
                            </div>
                        )}

                        {openPayments.length > 0 && (
                            <div class='table-responsive'>
                                <table class='table table-sm align-middle' style='font-size: 0.9em;'>
                                    <thead>
                                        <tr>
                                            <th>Dato</th>
                                            <th class='text-end'>Beløp</th>
                                            <th>Kunde i Tripletex</th>
                                            <th>Beskrivelse</th>
                                            <th>Bilag</th>
                                            <th>Forslag i Dyrejournal</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {openPayments.map(p => (
                                            <tr key={p.postingId}>
                                                <td class='text-nowrap'>{formatDate(p.date)}</td>
                                                <td class='text-end text-nowrap'>{formatAmount(Math.abs(p.amount))}</td>
                                                <td>{p.tripletexCustomerName}</td>
                                                <td>
                                                    {p.description}
                                                    {p.invoiceNumber > 0 && <small class='text-body-secondary ms-1'>(faktura #{p.invoiceNumber})</small>}
                                                </td>
                                                <td class='text-nowrap'>{p.voucherNumber ? `#${p.voucherNumber}` : '-'}</td>
                                                <td>
                                                    {p.registeredOnSale > 0 && (
                                                        <div class='text-success' style='font-size: 0.9em;'>
                                                            <i class='fa-solid fa-check-circle me-1' />
                                                            Registrert på salg #{p.registeredOnSaleNumber > 0 ? p.registeredOnSaleNumber : p.registeredOnSale}
                                                        </div>
                                                    )}
                                                    {!p.registeredOnSale && p.candidates.length === 0 && (
                                                        <small class='text-body-secondary'>Ingen forslag</small>
                                                    )}
                                                    {!p.registeredOnSale && p.candidates.map((c, idx) => (
                                                        <div key={c.id} class='d-flex align-items-center gap-2 mb-1 flex-wrap'>
                                                            {c.tripletexTransferred ? (
                                                                <span class='badge bg-success-subtle text-success-emphasis' style='font-size: 0.8em;'>
                                                                    <i class='fa-solid fa-check me-1' />
                                                                    I Tripletex{c.tripletexInvoiceNumber > 0 ? ` (faktura #${c.tripletexInvoiceNumber})` : ''} — koble i Tripletex
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        type='button'
                                                                        class='btn btn-sm btn-info py-0 rounded-pill'
                                                                        style='font-size: 0.85em; white-space: nowrap;'
                                                                        disabled={working[p.postingId] || bulkRunning}
                                                                        onClick={() => this.sendToTripletex(p, c)}
                                                                    >
                                                                        {working[p.postingId] ? <i class='fa-duotone fa-spinner fa-spin me-1' /> : <i class='fa-duotone fa-cloud-arrow-up me-1' />}
                                                                        Send til Tripletex
                                                                    </button>
                                                                    <button
                                                                        type='button'
                                                                        class='btn btn-sm btn-outline-success py-0 rounded-pill'
                                                                        style='font-size: 0.8em; white-space: nowrap;'
                                                                        disabled={working[p.postingId] || bulkRunning}
                                                                        onClick={() => this.registerPayment(p, c)}
                                                                        title='Registrer betalingen bare i Dyrejournal, uten å sende salget til Tripletex'
                                                                    >
                                                                        <i class='fa-duotone fa-money-check-dollar-pen me-1' />
                                                                        Kun registrer i DJ
                                                                    </button>
                                                                </>
                                                            )}
                                                            <button
                                                                type='button'
                                                                class='btn btn-sm btn-outline-primary py-0 rounded-pill'
                                                                style='font-size: 0.85em; white-space: nowrap;'
                                                                onClick={() => gotoSale && gotoSale(c.id)}
                                                            >
                                                                <i class='fa-duotone fa-cart-shopping me-1' />
                                                                Salg #{c.saleNumber}
                                                            </button>
                                                            <span class='text-nowrap'>{c.customerName}</span>
                                                            <small class='text-body-secondary text-nowrap'>
                                                                {formatDate(c.saleDate)} · rest {formatAmount(c.restPayment)} kr
                                                            </small>
                                                            {p.confident && idx === 0 && (
                                                                <span class='badge bg-success-subtle text-success-emphasis' style='font-size: 0.75em;'>
                                                                    Sikkert treff
                                                                </span>
                                                            )}
                                                            {c.reasons.map(r => (
                                                                <span key={r} class='badge bg-info-subtle text-info-emphasis' style='font-size: 0.75em;'>
                                                                    {REASON_LABELS[r] || r}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ))}
                                                    {results[p.postingId] && (
                                                        <small class={results[p.postingId].ok ? 'text-success' : 'text-danger'}>
                                                            <i class={`fa-solid ${results[p.postingId].ok ? 'fa-check' : 'fa-triangle-exclamation'} me-1`} />
                                                            {results[p.postingId].message}
                                                        </small>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {otherAccounts.length > 0 && (
                            <div class='mt-3'>
                                <h6>Andre åpne poster (uten kunde)</h6>
                                <p class='text-body-secondary mb-2' style='font-size: 0.85em;'>
                                    Poster som står åpne på andre kontoer, f.eks. innbetalinger regnskapsfører har
                                    parkert på en mellomkonto i påvente av kobling.
                                </p>
                                <div class='table-responsive'>
                                    <table class='table table-sm' style='font-size: 0.9em; max-width: 500px;'>
                                        <thead>
                                            <tr>
                                                <th>Konto</th>
                                                <th class='text-end'>Antall</th>
                                                <th class='text-end'>Sum</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {otherAccounts.map(a => (
                                                <tr key={a.accountNumber}>
                                                    <td>{a.accountNumber} {a.accountName}</td>
                                                    <td class='text-end'>{a.count}</td>
                                                    <td class='text-end'>{formatAmount(a.sumAmount)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>}
                </div>
            </div>
        );
    }
}

export default TripletexOpenPosts;
